import { Button, Checkbox, Form, Input, message, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import moment from 'moment';
import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { storageService } from '../../lib/db/StorageService';
import { AdminStatuses } from '../../lib/models/GeneralModels';
import { orderObject } from '../../lib/Utils';
import { Web3Service } from '../../lib/web3/Web3Service';
import { connectionAccountState, statusesState } from '../../recoil/atoms';
import { FloatingInputLabel } from '../FloatingInputLabel';

interface IStatusWrapper {
  title?: string;
  status: AdminStatuses;
  children: React.ReactNode | React.ReactNode[];
}

const StatusWrapper: FC<IStatusWrapper> = ({
  title,
  status,
  children,
}: IStatusWrapper) => {
  const currentAccount = useRecoilValue(connectionAccountState);
  const [statuses, setStatuses] = useRecoilState(statusesState);

  const [updating, setUpdating] = useState(false);
  const [isChecked, setIsChecked] = useState(status.display || false);

  const onChange = ({ target }: CheckboxChangeEvent) => {
    const { checked } = target;
    setIsChecked(checked);
  };

  const onUpdate = async () => {
    setUpdating(true);

    const { connection } = window;
    const web3Service = new Web3Service(connection);

    const payload: {
      timestamp: string;
      walletAddress: string;
      name: string;
      category: string;
      type: 'warning' | 'success' | 'error';
      message: string;
      externalLink: string;
      signature?: string;
      display: boolean;
    } = {
      timestamp: moment().format(),
      walletAddress: currentAccount.address,
      name: status.name,
      category: status.category,
      type: status.type,
      message: status.message,
      externalLink: status.externalLink,
      display: isChecked,
    };

    const signResult = await web3Service.signPersonalMessage(
      JSON.stringify(orderObject(payload)),
      currentAccount.address,
    );

    if (!signResult || !signResult?.signature) {
      message.warn('Unable to get signature to update status');
      setUpdating(false);
      return;
    }

    payload.signature = signResult.signature;

    const response = await storageService.updateStatus(+status.id, payload);

    if (!response) {
      setUpdating(false);
      message.warn('Unable to update the content of the status');
      return;
    }

    const updatedStatuses = [...statuses];
    const statusIndex = updatedStatuses.findIndex(
      (updatedStatus) => updatedStatus.id === response.status.id,
    );

    updatedStatuses[statusIndex] = response.status;

    setUpdating(false);
    setStatuses(updatedStatuses);
    message.success('The content of the status was updated successfully');
  };

  return (
    <div className="admin-status-card">
      <Form onFinish={onUpdate}>
        <div className="admin-status-card-title">{title}</div>

        {children}

        <Checkbox
          checked={isChecked}
          indeterminate={isChecked}
          className="custom-indeterminate-checkbox"
          onChange={onChange}
        >
          <span className="admin-status-card-checkbox">Display message</span>
        </Checkbox>

        <Button
          type="primary"
          className="nft-card-buy-button"
          htmlType="submit"
          style={{ width: '100%' }}
          disabled={updating}
          loading={updating}
        >
          <span className="normal-text-15">Update</span>
        </Button>
      </Form>
    </div>
  );
};

StatusWrapper.defaultProps = {
  title: 'Status Message',
};

const Status = ({
  status,
  hasLink = true,
  title = 'Status Message',
}: {
  status: AdminStatuses;
  hasLink?: boolean;
  title?: string;
}) => {
  const [formData, setFormData] = useState({ ...status });

  const onChange = ({ target }) => {
    const { name, value } = target;
    setFormData((form) => ({ ...form, [name]: value }));
  };

  return (
    <StatusWrapper status={formData} title={title}>
      <FloatingInputLabel
        label="Message"
        value={formData.message}
        style={
          {
            '--input-height': hasLink ? '81px' : '159px',
            marginBottom: hasLink ? undefined : '5px',
          } as CSSProperties
        }
      >
        <Form.Item>
          <Input.TextArea
            rows={hasLink ? 2 : 5}
            className="create-nft-input-description no-resize"
            value={formData.message}
            name="message"
            allowClear
            onChange={onChange}
          />
        </Form.Item>
      </FloatingInputLabel>

      {(hasLink && (
        <FloatingInputLabel
          label="Link"
          value={formData.externalLink}
          style={{ marginBottom: '5px' }}
        >
          <Form.Item>
            <Input
              className="input-float"
              name="externalLink"
              type="url"
              value={formData.externalLink}
              onChange={onChange}
            />
          </Form.Item>
        </FloatingInputLabel>
      )) || <div />}
    </StatusWrapper>
  );
};

const AdminStatus = () => {
  const [statuses, setStatuses] = useRecoilState(statusesState);
  const [loading, setLoading] = useState(false);

  const fetchStatuses = useCallback(async () => {
    if (!statuses.length) {
      setLoading(true);
      const response = await storageService.getAllStatuses();

      setLoading(false);

      if (response) {
        setStatuses(response.statuses);
      }
    }
  }, [statuses]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  if (loading) {
    return (
      <div
        className="flex"
        style={{
          justifyContent: 'center',
          flexDirection: 'column',
          height: '415px',
        }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap"
      style={{ paddingTop: '1rem', paddingBottom: '2rem' }}
    >
      {statuses.map((status) => (
        <Status
          status={status}
          key={status.id}
          hasLink={!!status.externalLink}
          title={status.name}
        />
      ))}
    </div>
  );
};

export default AdminStatus;
